import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/** Client-side cap on how long we treat a validation run as locking the UI (Firestore may stay incomplete if the backend stalls). */
export const VALIDATION_RUN_TIMEOUT_MS = 2 * 60 * 1000;

const CommunityValidationLockContext = createContext({
    validationUiLocked: false,
    validationTimedOut: false,
    serverSaysValidationPending: false,
    lastValidationRun: {},
});

export const useCommunityValidationLock = () => useContext(CommunityValidationLockContext);

export function CommunityValidationLockProvider({ lastValidationRun = {}, children }) {
    const [validationTimedOut, setValidationTimedOut] = useState(false);

    useEffect(() => {
        const pending = lastValidationRun?.complete === false && lastValidationRun?.id != null;
        if (!pending) {
            setValidationTimedOut(false);
            return undefined;
        }
        setValidationTimedOut(false);
        const timer = window.setTimeout(() => setValidationTimedOut(true), VALIDATION_RUN_TIMEOUT_MS);
        return () => window.clearTimeout(timer);
    }, [lastValidationRun?.id, lastValidationRun?.complete]);

    const serverSaysValidationPending =
        lastValidationRun?.complete === false && lastValidationRun?.id != null;

    const validationUiLocked = serverSaysValidationPending && !validationTimedOut;

    const value = useMemo(
        () => ({
            validationUiLocked,
            validationTimedOut,
            serverSaysValidationPending,
            lastValidationRun,
        }),
        [validationUiLocked, validationTimedOut, serverSaysValidationPending, lastValidationRun]
    );

    return (
        <CommunityValidationLockContext.Provider value={value}>{children}</CommunityValidationLockContext.Provider>
    );
}
